import { createOpencode, createOpencodeClient } from "@opencode-ai/sdk";
//#region src/process/server.ts
/** Locate the session id an OpenCode event belongs to, when it carries one. */
function sessionIdOf(event) {
	const props = event.properties;
	if (props?.sessionID !== void 0) return props.sessionID;
	if (event.type === "message.part.updated") return event.properties.part.sessionID;
	if (event.type === "message.updated") return event.properties.info.sessionID;
	if (event.type === "permission.updated") return event.properties.sessionID;
}
function buildConfig(options) {
	const mcp = { ...options.config?.mcp };
	for (const server of options.mcpServers ?? []) mcp[server.name] = {
		type: "remote",
		url: server.url,
		enabled: true
	};
	return {
		...options.config,
		...Object.keys(mcp).length > 0 && { mcp },
		...options.permission !== void 0 && { permission: options.permission }
	};
}
/**
* Boot (or attach to) an OpenCode HTTP server, resolve a session, and wire its
* event subscription + permission replies.
*
* This module is the only place that touches `@opencode-ai/sdk`; the rest of
* the package works with the structural types in `sdk-types.ts`.
*
* Resume semantics: when `resumeSessionId` is set and the server still knows
* the session (same machine, same data dir), it is reused. Otherwise a fresh
* session is created and `resumed: false` tells the adapter to send the
* flattened transcript.
*/
async function startOpencodeSession(options) {
	const { directory } = options;
	const dirQuery = directory !== void 0 ? { directory } : {};
	let client;
	let ownedServer;
	if (options.baseUrl !== void 0) client = createOpencodeClient({
		baseUrl: options.baseUrl,
		...options.headers !== void 0 && { headers: options.headers },
		...directory !== void 0 && { directory }
	});
	else {
		const config = buildConfig(options);
		const result = await createOpencode({
			...options.hostname !== void 0 && { hostname: options.hostname },
			...options.port !== void 0 && { port: options.port },
			...Object.keys(config).length > 0 && { config }
		});
		client = result.client;
		ownedServer = result.server;
	}
	const lifecycle = { disposed: false };
	const teardown = async () => {
		if (lifecycle.disposed) return;
		lifecycle.disposed = true;
		ownedServer?.close();
		await Promise.resolve();
	};
	try {
		let sessionId;
		let resumed = false;
		if (options.resumeSessionId !== void 0) {
			if ((await client.session.get({
				path: { id: options.resumeSessionId },
				query: dirQuery
			})).data) {
				sessionId = options.resumeSessionId;
				resumed = true;
			}
		}
		if (sessionId === void 0) sessionId = (await client.session.create({
			query: dirQuery,
			body: {},
			throwOnError: true
		})).data.id;
		const resolvedSessionId = sessionId;
		const handlePermission = async (permission) => {
			try {
				const response = await options.onPermissionRequest({
					id: permission.id,
					sessionID: permission.sessionID,
					type: permission.type,
					title: permission.title,
					...permission.callID !== void 0 && { callID: permission.callID }
				});
				await client.postSessionIdPermissionsPermissionId({
					path: {
						id: permission.sessionID,
						permissionID: permission.id
					},
					query: dirQuery,
					body: { response },
					throwOnError: true
				});
			} catch (error) {
				if (!lifecycle.disposed) options.onError?.(error);
			}
		};
		const stream = (await client.event.subscribe()).stream;
		(async () => {
			try {
				for await (const event of stream) {
					if (lifecycle.disposed) break;
					const sid = sessionIdOf(event);
					if (sid !== void 0 && sid !== resolvedSessionId) continue;
					if (event.type === "permission.updated") {
						handlePermission(event.properties);
						continue;
					}
					options.onEvent(event);
				}
			} catch (error) {
				if (!lifecycle.disposed) options.onError?.(error);
			}
		})();
		return {
			sessionId: resolvedSessionId,
			resumed,
			prompt: async (text) => {
				const data = (await client.session.prompt({
					path: { id: resolvedSessionId },
					query: dirQuery,
					body: {
						model: {
							providerID: options.providerID,
							modelID: options.modelID
						},
						parts: [{
							type: "text",
							text
						}]
					},
					throwOnError: true
				})).data;
				return {
					message: data.info,
					text: data.parts.filter((part) => part.type === "text").map((part) => part.text).join("")
				};
			},
			abort: async () => {
				try {
					await client.session.abort({
						path: { id: resolvedSessionId },
						query: dirQuery
					});
				} catch {}
			},
			dispose: async () => {
				lifecycle.disposed = true;
				try {
					await stream.return(void 0);
				} catch {}
				ownedServer?.close();
			}
		};
	} catch (error) {
		await teardown();
		throw error;
	}
}
//#endregion
export { startOpencodeSession };

//# sourceMappingURL=server.js.map